/* Enhancements keep Shopify's native variant and cart handling. */
if (!customElements.get('gg-option-search')) {
  customElements.define('gg-option-search', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const fieldset = this.closest('fieldset');
      const search = this.querySelector('input');
      if (!fieldset || !search) return;
      search.addEventListener('change', event => event.stopPropagation());
      search.addEventListener('keydown', event => {
        if (event.key === 'Enter') event.preventDefault();
        if (event.key === 'Escape') { search.value = ''; search.dispatchEvent(new Event('input')); }
      });
      search.addEventListener('input', () => {
        const query = search.value.trim().toLocaleLowerCase('da');
        let matches = 0;
        fieldset.querySelectorAll('input[type="radio"]').forEach(radio => {
          const match = radio.value.toLocaleLowerCase('da').includes(query);
          radio.classList.toggle('gg-filter-hidden', !match);
          const label = radio.nextElementSibling;
          if (label?.tagName === 'LABEL') label.classList.toggle('gg-filter-hidden', !match);
          if (match) matches++;
        });
        this.querySelector('[role="status"]').hidden = matches !== 0;
      });
    }
  });
}
if (!customElements.get('gg-mobile-purchase')) {
  customElements.define('gg-mobile-purchase', class extends HTMLElement {
    connectedCallback() {
      this.update = () => {
        const info = document.getElementById(`ProductInfo-${this.dataset.section}`);
        const button = info?.querySelector('.product-form__submit');
        this.hidden = !(button && button.getBoundingClientRect().bottom < 0);
      };
      this.querySelector('a').addEventListener('click', event => {
        const info = document.getElementById(`ProductInfo-${this.dataset.section}`);
        const target = info?.querySelector('variant-selects') || info?.querySelector('.product-form__submit');
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({behavior: 'auto', block: 'center'});
        (target.querySelector('input:checked, select') || target).focus({preventScroll: true});
      });
      window.addEventListener('scroll', this.update, {passive:true});
      window.addEventListener('resize', this.update);
      this.update();
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.update);
      window.removeEventListener('resize', this.update);
    }
  });
}

/* Optional care recommendations. Prices, availability and eligibility are server rendered. */
if (!customElements.get('gg-cart-care')) {
  customElements.define('gg-cart-care', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.onSubmit = event => {
        if (event.target.matches('[data-gg-care-form]')) this.addCare(event);
      };
      this.addEventListener('submit', this.onSubmit);
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
        if (event.source !== 'gg-cart-care') this.querySelector('[role="status"]').hidden = true;
        return this.refresh();
      });
    }
    disconnectedCallback() {
      this.unsubscribe?.();
      this.refreshController?.abort();
      this.removeEventListener('submit', this.onSubmit);
      this.ready = false;
    }
    async refresh() {
      this.refreshController?.abort();
      this.refreshController = new AbortController();
      try {
        const response = await fetch(`${routes.cart_url}?section_id=gg-cart-care`, {signal:this.refreshController.signal});
        if (!response.ok) throw new Error('Cart refresh failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = html.querySelector('[data-gg-care-content]');
        if (content && this.isConnected) this.querySelector('[data-gg-care-content]').innerHTML = content.innerHTML;
      } catch (error) {
        // Do not leave an old shipping promise visible if totals could not be refreshed.
        if (error.name !== 'AbortError') this.querySelector('.gg-free-shipping')?.remove();
      }
    }
    async addCare(event) {
      event.preventDefault();
      if (this.busy) return;
      this.busy = true;
      const form = event.target;
      const button = form.querySelector('button[type="submit"]');
      const status = this.querySelector('[role="status"]');
      const originalLabel = button.textContent;
      button.disabled = true;
      button.textContent = 'Tilføjer…';
      status.hidden = true;
      const footer = document.getElementById('main-cart-footer');
      const sections = ['cart-icon-bubble', 'cart-notification-button'];
      if (footer) sections.push(footer.dataset.id);
      try {
        const data = new FormData(form);
        data.delete('return_to');
        data.append('sections', sections.join(','));
        data.append('sections_url', window.location.pathname);
        const addUrl = new URL(routes.cart_add_url, window.location.origin);
        if (!addUrl.pathname.endsWith('.js')) addUrl.pathname += '.js';
        const response = await fetch(addUrl.pathname + addUrl.search, {method:'POST',headers:{'Accept':'application/json','X-Requested-With':'XMLHttpRequest'},body:data});
        const result = await response.json();
        if (!response.ok || result.status) {
          const error = new Error(result.description || 'Produktet kunne ikke tilføjes.');
          error.confirmedRejection = true;
          throw error;
        }
        // Update totals and count without opening another notification or moving the customer.
        for (const id of ['cart-icon-bubble', 'cart-notification-button']) {
          const target = document.getElementById(id);
          const markup = result.sections?.[id];
          if (target && markup) {
            const source = new DOMParser().parseFromString(markup,'text/html').querySelector('.shopify-section');
            if (source) target.innerHTML = source.innerHTML;
          }
        }
        if (footer && result.sections?.[footer.dataset.id]) {
          const html = new DOMParser().parseFromString(result.sections[footer.dataset.id],'text/html');
          const source = html.querySelector('.js-contents');
          const target = footer.querySelector('.js-contents');
          if (source && target) target.innerHTML = source.innerHTML;
        }
        status.textContent = form.dataset.successMessage || 'Produktet er lagt i kurven.';
        status.hidden = false;
        status.focus({preventScroll:true});
        form.closest('.gg-care-offer')?.remove();
        // Existing cart components refresh line items; recommendations refresh their conditions.
        publish(PUB_SUB_EVENTS.cartUpdate, {source:'gg-cart-care',cartData:result}).catch(() => {});
      } catch (error) {
        status.hidden = false;
        if (error.confirmedRejection) {
          status.textContent = error.message;
          button.disabled = false;
          button.textContent = originalLabel;
        } else {
          // A lost/non-JSON response can occur after the add succeeded. Never retry automatically.
          status.textContent = 'Vi kunne ikke bekræfte opdateringen. Tjek kurven, før du tilføjer igen. ';
          const link = document.createElement('a');
          link.href = routes.cart_url;
          link.textContent = 'Se kurven';
          status.append(link);
          button.textContent = 'Tjek kurven';
        }
      } finally { this.busy = false; }
    }
  });
}

